// ============================================
// Pain-led landing page template (G2M batch 1)
//
// One template, many pages: each funnel page is a config object
// (see FirstPeptidePage / ReconstitutionPage). Copy source:
// docs/g2m-2026-07/02_landing_page_copy.md with the CTA doctrine
// applied (docs/2026-07-12-g2m-plan-v2.md Section 4/5).
// Visual language: reuses the LandingPage `lp-*` system + funnel.css.
// ============================================

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { usePrerenderSignal } from '../../../utils/usePrerenderSignal';
import { trackEvent } from '../../../utils/analytics';
import '../LandingPage.css';
import './funnel.css';
import { SITE_ORIGIN, appStoreLive, captureTrafficSource, getTrafficSource, isAutomatedBrowser } from './marketingConfig';
import {
  FunnelAppStoreBadge,
  FunnelEmailCapture,
  FunnelFooter,
  FunnelInlineCapture,
  FunnelNav,
  FunnelPartnerBanner,
  FunnelStickyCta,
  FunnelTrustStrip,
} from './MarketingCta';
import type { MagnetInfo } from './MarketingCta';

export type PainQuote = { text: string; attribution: string };

export type PainPageConfig = {
  slug: string;
  path: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: React.ReactNode;
  /** Plain-text H1 for JSON-LD / OG */
  h1Text: string;
  sub: string;
  heroCtaLabel: string;
  sections: { heading: string; lead: string; body: React.ReactNode; quote?: PainQuote }[];
  steps: { title: string; body: string }[];
  reassurance: { heading: string; body: string };
  /** `defaultOpen` renders an objection-critical FAQ expanded on load (finding F4). */
  faqs: { q: string; a: string; defaultOpen?: boolean }[];
  magnet: MagnetInfo;
  magnetHeadline: string;
  magnetLede: string;
  /** Secondary free-content link under the hero CTA */
  secondary: { label: string; to: string };
};

const EMAIL_SECTION_ID = 'get-the-guide';

// Pre-approval fallback next step after a capture (finding F6). Post-approval
// this is ignored in favour of the App Store badge (see CaptureNextStep).
const NEXT_STEP = { label: 'Or try the free calculator', to: '/tools/reconstitution-calculator' };

export const PainPage: React.FC<{ config: PainPageConfig }> = ({ config }) => {
  usePrerenderSignal();
  // Multi-open accordion so objection-critical FAQs can start expanded (F4).
  const [openFaqs, setOpenFaqs] = useState<Set<number>>(() => {
    const defaults = config.faqs.reduce<number[]>((acc, faq, i) => {
      if (faq.defaultOpen) acc.push(i);
      return acc;
    }, []);
    return new Set(defaults.length ? defaults : [0]);
  });
  const toggleFaq = (i: number) => setOpenFaqs((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  useEffect(() => {
    captureTrafficSource();
    if (isAutomatedBrowser()) return; // prerender/bot traffic: no analytics
    void trackEvent('marketing_page_view', { page: config.slug, source: getTrafficSource(config.slug) });
  }, [config.slug]);

  const canonical = `${SITE_ORIGIN}${config.path}`;
  const ogImage = `${SITE_ORIGIN}/images/og-landing-v2.png`;

  return (
    <div className="landing fp-page">
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={config.metaTitle} />
        <meta property="og:description" content={config.metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.metaTitle} />
        <meta name="twitter:description" content={config.metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: config.faqs.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        })}</script>
      </Helmet>

      <FunnelNav page={config.slug} />
      <FunnelPartnerBanner />

      {/* Hero: copy-led, no phone carousel */}
      <section className="lp-hero fp-hero">
        <div className="lp-hero-inner fp-hero-inner">
          <div className="lp-hero-copy fp-hero-copy">
            <span className="lp-section-head__eyebrow">{config.eyebrow}</span>
            <h1>{config.h1}</h1>
            <p className="lp-hero-lede">{config.sub}</p>
            {/* Capture lives in the hero now, not six sections down (finding F1). */}
            <div className="fp-hero-cta">
              {appStoreLive() ? (
                <div className="lp-hero-actions fp-hero-actions">
                  <FunnelAppStoreBadge size="lg" page={config.slug} />
                  <Link className="fp-secondary-link" to={config.secondary.to}>{config.secondary.label}</Link>
                </div>
              ) : (
                <>
                  <FunnelInlineCapture
                    page={config.slug}
                    sourceKey={config.slug}
                    placement="hero"
                    buttonLabel={config.heroCtaLabel}
                    ariaLabel={config.heroCtaLabel}
                    magnet={config.magnet}
                    successLead="Thanks, it is on the way."
                    nextStep={NEXT_STEP}
                    className="fp-hero-capture"
                  />
                  <Link className="fp-secondary-link fp-hero-secondary" to={config.secondary.to}>{config.secondary.label}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Three narrative sections */}
      {config.sections.map((s) => (
        <section className="fp-section" key={s.heading}>
          <div className="fp-section-inner">
            <div className="lp-section-head">
              <span className="lp-section-head__eyebrow">{s.heading}</span>
              <h2 className="lp-section-head__title">{s.lead}</h2>
            </div>
            <div className="fp-section-body">{s.body}</div>
            {s.quote && (
              <figure className="fp-quote">
                <blockquote>{s.quote.text}</blockquote>
                <figcaption>{s.quote.attribution}</figcaption>
              </figure>
            )}
          </div>
        </section>
      ))}

      {/* How it works */}
      <section className="lp-features fp-steps">
        <div className="lp-features-inner">
          <div className="lp-section-head">
            <span className="lp-section-head__eyebrow">How it works</span>
            <h2 className="lp-section-head__title">Three steps. No guesswork.</h2>
          </div>
          <div className="lp-features-grid fp-steps-grid">
            {config.steps.map((step, i) => (
              <article key={step.title} className="lp-feature-card">
                <h3 className="lp-feature-card__title">{step.title}</h3>
                <p className="lp-feature-card__body">{step.body}</p>
                <div className="lp-feature-card__spacer" />
                <div className="lp-feature-card__tag">Step {i + 1}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Reassurance beat */}
      <section className="fp-section fp-reassurance">
        <div className="fp-section-inner">
          <div className="lp-section-head lp-section-head--center">
            <span className="lp-section-head__eyebrow">Take a breath</span>
            <h2 className="lp-section-head__title">{config.reassurance.heading}</h2>
            <p className="lp-section-head__lede">{config.reassurance.body}</p>
          </div>
        </div>
      </section>

      {/* Source-citation trust strip: social proof, homepage-parity (finding F3). */}
      <FunnelTrustStrip />

      {/* FAQ */}
      <section className="lp-faq">
        <div className="lp-faq-inner">
          <div className="lp-section-head">
            <span className="lp-section-head__eyebrow">FAQ</span>
            <h2 className="lp-section-head__title">Fair questions.</h2>
          </div>
          <div className="lp-faq-list">
            {config.faqs.map((item, i) => {
              const isOpen = openFaqs.has(i);
              return (
                <div key={item.q} className={`lp-faq-item ${isOpen ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="lp-faq-button"
                    onClick={() => toggleFaq(i)}
                    aria-expanded={isOpen}
                  >
                    <span className="lp-faq-q">{item.q}</span>
                    <span className="lp-faq-toggle">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  </button>
                  {isOpen && <div className="lp-faq-answer">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Email capture + magnet (kept as the canonical bottom form) */}
      <FunnelEmailCapture
        id={EMAIL_SECTION_ID}
        page={config.slug}
        headline={config.magnetHeadline}
        lede={config.magnetLede}
        magnet={config.magnet}
        nextStep={NEXT_STEP}
      />

      <FunnelFooter />

      {/* Sticky mobile CTA: appears after ~50% scroll, hides when the form shows (F2). */}
      <FunnelStickyCta page={config.slug} label={config.heroCtaLabel} targetId={EMAIL_SECTION_ID} />
    </div>
  );
};
